<template>
  <div
    class="form-control-store"
    :data-id="field.id"
    :data-path="`${path}-${field.id}`"
    :class="[{ 'server-message': serverMessage.type }, 'server-' + serverMessage.type]"
  >
    <div @keyup="onKeyup">
      <component :is="activeComponent" v-bind="props" v-on="$listeners"></component>
    </div>
  </div>
</template>

<script>
import { isSelect, isPaint, isSignature, isCameras } from '../../form'
import EventBus from '../../utils/event-bus'
import { filter } from 'lodash'
import { DEFAULT_STORE_COMPONENT, STORE_COMPONENT_BY_TYPE } from '@/components/form/helpers/components'
import { FIELD_TYPES } from '../../consts'

/**
 * @class FormControlStore
 *
 * @description This component is the state handler/wrapper for FormControl
 */
export default {
  name: 'form-control-store',
  $_veeValidate: {
    name() {
      return this.validationName
    },
    value() {
      return isSelect(this.field) ? this.field.formattedValue : this.field.value
    },
  },
  props: ['path', 'groupReadOnly', 'type'],
  computed: {
    props() {
      const props = {
        ...this.$props,
        validationName: this.validationName,
        isInvalid: this.isFieldInvalid,
        disabled: this.isReadOnly(),
      }

      if (this.isReadView) props.field = this.field

      switch (this.field.type) {
        case FIELD_TYPES.DROPDOWN:
        case FIELD_TYPES.MULTISELECT:
        case FIELD_TYPES.STATUS:
          props.placeholder = this.placeholder
          props.createNewElement = this.createNewElementSelect(this.field)
          break

        case FIELD_TYPES.SQL_CHOSEN:
          props.createNewElement = this.createNewElementSelect(this.field)
          break
        case FIELD_TYPES.CAMERAS:
          props.multiple = this.field.multiselect
          props.filter = this.filterCameras
          props.filterCameras = this.filterCameras
          break
        case FIELD_TYPES.LABEL:
          props.asModal = false
          break
        case FIELD_TYPES.SIGNATURE:
          props.filter = this.filterCameras
          break
        case FIELD_TYPES.W_PAINT:
          props.filter = this.filterCameras
          break
        case FIELD_TYPES.CHECKBOX_LIST:
        case FIELD_TYPES.RADIO_BUTTON_LIST:
          props.createNewElement = this.createNewElementSelect(this.field)
          break
        default:
          break
      }

      return props
    },
    componentName() {
      return this.getComponentName(this.type)
    },
    activeComponent() {
      return () => import(`${this.isReadView ? './FormControlReadView' : `./store-controls/${this.componentName}`}`)
    },
    messages() {
      return this.$get('messages') || []
    },
    isReadView() {
      return this.$get('isReadView')
    },
    validationName() {
      return `${this.$storeNamespace}-${this.idOrField}`
    },
    isFieldInvalid() {
      return (
        filter(this.$get('messages'), {
          name: this.field.name,
          type: 'error',
        }).length > 0
      )
    },
    serverMessage() {
      const message = this.messages.filter((x) => !!x.target).find((x) => x.name === this.field.name)
      if (message) {
        return message
      }
      return {}
    },
    field() {
      return this.$get('fieldByPath')(this.path)
    },
    fieldProps() {
      return Object.assign(this.$props, { validationName: this.validationName }, { isInvalid: this.isFieldInvalid })
    },
    idOrField() {
      return this.field.id || this.field.name
    },
    placeholder() {
      return this.field.placeholder
    },
    filterCameras() {
      if (!(isCameras(this.field) || isSignature(this.field) || isPaint(this.field))) {
        return {}
      }

      const temp = {
        mainTableName: this.$get('form').mainTable,
        mainTableID: this.$get('idMainTable'),
        formId: this.$get('form').id,
        fieldID: this.field.id,
        fieldName: this.field.name,
      }
      const where = {
        $and: [
          {
            $or: [
              {
                $and: [
                  {
                    'foreignKey.mainTableName': temp.mainTableName,
                    $or: [{ 'foreignKey.fieldID': temp.fieldID }, { 'foreignKey.fieldName': temp.fieldName }],
                  },
                  {
                    $or: [
                      { 'foreignKey.mainTableID': String(temp.mainTableID) },
                      { 'foreignKey.mainTableID': Number(temp.mainTableID) },
                    ],
                  },
                ],
              },
              {
                references: {
                  $elemMatch: {
                    mainTableName: temp.mainTableName,
                    formId: temp.formId,
                    $and: [
                      {
                        $or: [{ mainTableID: String(temp.mainTableID) }, { mainTableID: Number(temp.mainTableID) }],
                      },
                      {
                        $or: [{ fieldID: temp.fieldID }, { fieldName: temp.fieldName }],
                      },
                    ],
                  },
                },
              },
            ],
          },
        ],
      }

      if (this.$get('version')) {
        where.$and[1] = {
          'foreignKey.version': {
            $in: [this.$get('version')],
          },
        }
      } else {
        where.$and[1] = {
          active: true,
        }
      }

      return JSON.stringify({ where })
    },
  },
  methods: {
    isSelect,
    isPaint,
    isSignature,
    isCameras,
    isReadOnly() {
      return (
        this.field.readOnly == 1 ||
        this.groupReadOnly ||
        (Array.isArray(this.field.readOnly) && this.field.readOnly[0] == 1) ||
        this.isReadView
      )
    },
    onKeyup($event) {
      if ($event.key === 'Enter' && $event.target.type !== FIELD_TYPES.TEXTAREA) EventBus.$emit('enter-pressed')
    },
    createNewElementSelect(field) {
      return { createNewElement: field.createNewElement, crud: field.crud }
    },
    getComponentName(fieldType) {
      let type = fieldType

      switch (fieldType) {
        case FIELD_TYPES.RICH_TEXT:
          type = this.field.formattedText ? fieldType : FIELD_TYPES.TEXTAREA
      }

      return STORE_COMPONENT_BY_TYPE[type] || DEFAULT_STORE_COMPONENT
    },
  },
}
</script>
<style>
.disabled .form-control-store {
  margin-top: 0.5em;
}
</style>
